/**
 * Copy an entry as a plain-text stat block or GitHub-flavored Markdown (#195).
 *
 * ONE formatter, driven by the Family registry — not ten. The issue proposed
 * "a client-side formatter per entity type", but there are no per-entity detail
 * pages any more: everything renders through `GenericDetail` from the derived
 * registry entry, and the registry already declares exactly what a stat block
 * needs — the attributes, their labels, datatypes, formats/units, grouping,
 * section styles and display order. So this module walks the same
 * `family.detail.sections` the page renders, in the same order, and a Family a
 * user authors this afternoon copies correctly with no code written for it.
 *
 * IT REUSES THE RENDERER'S OWN SPELLINGS rather than growing a second opinion:
 * `formatScalar` (number format/unit/prefix — which is also what keeps a price
 * in its AUTHORED denomination: the detail payload carries the authored value
 * and the attribute carries the authored unit, so a Traveller ship authored
 * `37.08` at `unit: "MCr"` copies as `37.08 MCr`, never the register's
 * normalized base-unit number), `composeLine`, `linkDataText`,
 * `composedReferenceParts`, `partsText`, `signedListText`, `abbrevText`, and
 * `evaluateFormula` for the stat-grid modifier. If a value would be spelled
 * differently here than on the page, that is a bug.
 *
 * AUTHORED HTML IS UNTRUSTED and never becomes DOM here: a `richtext` value is
 * run through the same `sanitizeHtml` the renderer uses, parsed with
 * `DOMParser` (inert — nothing loads, nothing executes), and serialized to
 * text. Authored `<a href>`s are dropped and only their text kept — an
 * authored URL is untrusted input and a copied document is exactly where a
 * disguised link would travel.
 *
 * REFERENCES CARRY THEIR NAMES, and in Markdown become absolute links to the
 * site (`origin/segment?slug=…&system=…` — self-describing, per the #92 rule)
 * ONLY when the target family resolves a route and the target has a slug; a
 * slugless reference is plain text, never a link — the same rule the page and
 * `link-integrity.spec.js` keep.
 *
 * Two sections deliberately do not copy: `bucket-by-link-data` (a grid of
 * navigation links — "Spells by Level" — not facts about this entry) and the
 * bodies of `inline-expand` rows (an archetype's full prose belongs to the
 * archetype; the copy lists the names). The printings picker, templates panel
 * and "Also Appears In" are platform navigation, not entry content.
 */

import composeLine, { memberText } from '../components/generic/composeLine';
import formatScalar from '../components/generic/formatValue';
import {
  signedListText, abbrevText, linkDataText, composedReferenceParts, partsText,
} from '../components/generic/DetailSection';
import { badgeItems } from '../components/generic/AttrBadge';
import evaluateFormula, { formatSigned } from './displayFormula';
import { segmentForFamilyKey, filterRouteFor } from '../config/familyRegistry';
import sanitizeHtml from './sanitizeHtml';
import displayFormatText from './displayFormat';

function hasValue(v) {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

// ---------------------------------------------------------------------------
// Sanitised HTML → text (plain or Markdown-flavoured)
// ---------------------------------------------------------------------------

const BLOCK_TAGS = new Set([
  'p', 'div', 'ul', 'ol', 'table', 'blockquote', 'section', 'article',
]);
const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

function serializeChildren(node, md) {
  let out = '';
  for (const child of node.childNodes) out += serializeNode(child, md);
  return out;
}

function serializeNode(node, md) {
  if (node.nodeType === 3) return node.textContent.replace(/\s+/g, ' ');
  if (node.nodeType !== 1) return '';
  const tag = node.tagName.toLowerCase();
  if (tag === 'br') return '\n';
  if (tag === 'li') {
    const inner = serializeChildren(node, md).trim();
    return inner ? `- ${inner}\n` : '';
  }
  if (tag === 'tr') {
    const cells = [...node.children]
      .map((cell) => serializeChildren(cell, md).trim().replace(/\|/g, md ? '\\|' : '/'));
    return cells.length ? `${cells.join(' | ')}\n` : '';
  }
  if (tag === 'strong' || tag === 'b') {
    const inner = serializeChildren(node, md).trim();
    return inner ? (md ? `**${inner}**` : inner) : '';
  }
  if (tag === 'em' || tag === 'i') {
    const inner = serializeChildren(node, md).trim();
    return inner ? (md ? `*${inner}*` : inner) : '';
  }
  if (HEADING_TAGS.has(tag)) {
    const inner = serializeChildren(node, md).trim();
    return inner ? `\n${md ? `**${inner}**` : inner}\n\n` : '';
  }
  if (BLOCK_TAGS.has(tag)) {
    const inner = serializeChildren(node, md).trim();
    return inner ? `\n${inner}\n\n` : '';
  }
  // Inline containers (span, a, …): the text, never the markup. An authored
  // href is untrusted and is deliberately not carried into the copy.
  return serializeChildren(node, md);
}

/** Sanitised, inert HTML-to-text. Exported for tests. */
export function htmlToText(html, { markdown = false } = {}) {
  if (!hasValue(html)) return '';
  const doc = new DOMParser().parseFromString(sanitizeHtml(String(html)), 'text/html');
  return serializeChildren(doc.body, markdown)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---------------------------------------------------------------------------
// One entry, walked section by section
// ---------------------------------------------------------------------------

function escapeLabel(text) {
  return String(text).replace(/([[\]])/g, '\\$1');
}

/** A markdown link when the ctx has an origin-worthy target, else the name. */
function linkOrName(name, segment, slug, ctx) {
  if (!ctx.markdown || !segment || !slug) return String(name);
  const sys = ctx.system ? `&system=${encodeURIComponent(ctx.system)}` : '';
  return `[${escapeLabel(name)}](${ctx.origin}/${segment}?slug=${encodeURIComponent(slug)}${sys})`;
}

/** A reference/reverse value: names (linked in Markdown) + their link data. */
function referenceText(attr, value, ctx) {
  const segment = segmentForFamilyKey(attr.targetFamily, ctx.system);
  const targets = Array.isArray(value) ? value : [value];
  return targets
    .filter((t) => t && hasValue(t.name))
    .map((target) => {
      // The same seam the two page renderers read, so a copied stat block
      // cannot spell a prerequisite differently from the page it came from.
      const spelled = displayFormatText(attr, target);
      if (spelled !== null) return spelled;
      const data = linkDataText(attr, target);
      return `${linkOrName(target.name, segment, target.slug, ctx)}${data ? ` ${data}` : ''}`;
    })
    .join(', ');
}

/** The composed requirements sentence, with Markdown links where they resolve. */
function composedReferenceString(attr, value, ctx) {
  const separator = (attr.renderConfig || {}).separator || ', ';
  const segment = segmentForFamilyKey(attr.targetFamily, ctx.system);
  return composedReferenceParts(attr, Array.isArray(value) ? value : [])
    .map((p) => `${p.separator ? separator : ''}${p.before}`
      + `${linkOrName(p.name, segment, p.slug, ctx)}${p.parenthetical}${p.qualifiers}${p.after}`)
    .join('');
}

/** One scalar/array/reference value as text — the AttrValue rules. */
function valueText(attr, value, ctx) {
  if (attr.kind === 'reference' || attr.kind === 'reverse') {
    return attr.renderStyle === 'composed-line'
      ? composedReferenceString(attr, value, ctx)
      : referenceText(attr, value, ctx);
  }
  if (Array.isArray(value)) return value.map((v) => memberText(v)).filter(Boolean).join(', ');
  return formatScalar(attr, value);
}

/**
 * One attribute value as plain text — the seam the Compare view (issue 188)
 * shares with the copy formatter, so a compare cell spells a value exactly as
 * the detail page and the stat-block copy do. References become their names
 * (never links — a table cell link per name is noise), arrays join, and a
 * scalar goes through `formatScalar`, which is what keeps a price in its
 * AUTHORED denomination in a compare column too.
 */
export function attributeText(attr, value, { system = '' } = {}) {
  return valueText(attr, value, { markdown: false, origin: '', system });
}

function labeled(label, text, ctx) {
  return ctx.markdown ? `**${label}:** ${text}` : `${label}: ${text}`;
}

function heading(text, ctx) {
  return ctx.markdown ? `## ${text}` : text;
}

function proseBlock(attr, value, ctx) {
  if (attr.datatype === 'richtext') return htmlToText(value, ctx);
  return String(value).trim();
}

// --- the JsonTable document shape, as text ---------------------------------

function cellText(cell) {
  if (cell === undefined || cell === null) return '';
  if (typeof cell === 'object') return JSON.stringify(cell);
  return String(cell);
}

function oneTable(table, ctx) {
  const lines = [];
  const title = table.title || table.name || table.subtitle;
  if (title) lines.push(ctx.markdown ? `**${title}**` : title);
  if (table.description) lines.push(String(table.description));
  const columns = Array.isArray(table.columns) ? table.columns : null;
  const rows = Array.isArray(table.rows) ? table.rows : [];
  const rowText = (row) => (Array.isArray(row) ? row : [row]).map(cellText);
  if (ctx.markdown && columns) {
    lines.push(`| ${columns.map(cellText).join(' | ')} |`);
    lines.push(`| ${columns.map(() => '---').join(' | ')} |`);
    for (const row of rows) lines.push(`| ${rowText(row).join(' | ')} |`);
  } else {
    if (columns) lines.push(columns.map(cellText).join(' | '));
    for (const row of rows) lines.push(rowText(row).join(' | '));
  }
  const notes = table.notes || table.footnote;
  for (const note of Array.isArray(notes) ? notes : (notes ? [notes] : [])) {
    lines.push(String(note));
  }
  for (const [mark, note] of Object.entries(table.footnotes || {})) {
    lines.push(`${mark} ${note}`);
  }
  return lines.join('\n');
}

function tableSectionText(section, values, ctx) {
  const value = values[section.attr.key];
  if (!hasValue(value)) return '';
  let tables = [value];
  if (Array.isArray(value)) tables = value;
  else if (Array.isArray(value.tables)) tables = value.tables;
  const parts = tables.map((t) => oneTable(t, ctx)).filter(Boolean);
  if (!parts.length) return '';
  return [heading(section.heading, ctx), ...parts].join('\n\n');
}

// --- the section dispatch, mirroring DetailSection's ------------------------

function rowsSectionText(section, values, ctx) {
  const lines = [];
  for (const attr of section.members) {
    const value = values[attr.key];
    if (!hasValue(value)) continue;
    if (attr.isProse) {
      const text = proseBlock(attr, value, ctx);
      if (text) lines.push(text);
      continue;
    }
    const text = valueText(attr, value, ctx);
    if (text) lines.push(labeled(attr.label, text, ctx));
  }
  if (!lines.length) return '';
  return section.heading ? [heading(section.heading, ctx), ...lines].join('\n') : lines.join('\n');
}

function composedSectionText(section, values, ctx) {
  // The reference flavor (a class's requirements sentence as its own section).
  if (section.attr) {
    const value = values[section.attr.key];
    if (!hasValue(value)) return '';
    const text = composedReferenceString(section.attr, value, ctx);
    return text ? labeled(section.heading || section.attr.label, text, ctx) : '';
  }
  const config = section.config || {};
  if (Array.isArray(config.lines)) {
    const rows = config.lines
      .map((line) => ({ label: line.label, text: partsText(line.parts, config.separator, values, section.members) }))
      .filter((row) => row.text)
      .map((row) => labeled(row.label, row.text, ctx));
    if (!rows.length) return '';
    return section.heading ? [heading(section.heading, ctx), ...rows].join('\n') : rows.join('\n');
  }
  if (Array.isArray(config.parts)) {
    const text = partsText(config.parts, config.separator, values, section.members);
    return text ? labeled(section.heading, text, ctx) : '';
  }
  const text = composeLine(config.pattern, values);
  if (!text) return '';
  return section.heading ? `${labeled(section.heading, text, ctx)}` : text;
}

function statGridText(section, values, ctx) {
  const cells = [];
  for (const attr of section.members) {
    const n = Number(values[attr.key]);
    if (!hasValue(values[attr.key]) || !Number.isFinite(n) || n === 0) continue;
    const modifier = section.formula
      ? evaluateFormula(section.formula, { ...values, value: n }) : null;
    cells.push(`${attr.label} ${n}${Number.isFinite(modifier) ? ` (${formatSigned(modifier)})` : ''}`);
  }
  if (!cells.length) return '';
  return labeled(section.heading, cells.join(', '), ctx);
}

function linkListText(section, values, ctx) {
  const targets = values[section.attr.key];
  if (!Array.isArray(targets) || !targets.length) return '';
  const text = referenceText(section.attr, targets, ctx);
  return text ? labeled(section.heading, text, ctx) : '';
}

function badgeLinksText(section, values, ctx) {
  const config = section.config || {};
  const parts = [];
  for (const attr of section.members) {
    const value = values[attr.key];
    if (!hasValue(value)) continue;
    const route = attr.kind === 'reference'
      ? filterRouteFor(attr.targetFamily, config.link_filter_key, ctx.system) : null;
    for (const item of Array.isArray(value) ? value : [value]) {
      const name = item && typeof item === 'object' ? item.name : item;
      if (!name) continue;
      const slug = item && typeof item === 'object' ? item.slug : null;
      if (ctx.markdown && route && slug) {
        const sys = ctx.system ? `&system=${encodeURIComponent(ctx.system)}` : '';
        parts.push(`[${escapeLabel(name)}](${ctx.origin}/${route.segment}`
          + `?${route.urlKey}=${encodeURIComponent(slug)}${sys})`);
      } else {
        parts.push(String(name));
      }
    }
  }
  return parts.length ? labeled(section.heading, parts.join(', '), ctx) : '';
}

function abbrevSectionText(section, values, ctx) {
  const flags = abbrevText(section.members, values);
  const tails = section.members
    .filter((m) => m.datatype !== 'boolean' && hasValue(values[m.key]))
    .map((m) => ` (${values[m.key]})`)
    .join('');
  return labeled(section.heading, `${flags}${tails}`, ctx);
}

function inlineExpandText(section, values, ctx) {
  const rows = values[section.attr.key];
  if (!Array.isArray(rows) || !rows.length) return '';
  const items = rows
    .filter((row) => row && hasValue(row.name))
    .map((row) => `- ${row.name}${row.rulebook_name ? ` (${row.rulebook_name})` : ''}`);
  if (!items.length) return '';
  return [heading(`${section.heading} (${items.length})`, ctx), ...items].join('\n');
}

function proseSectionText(section, values, ctx) {
  const value = values[section.attr.key];
  if (!hasValue(value)) return '';
  const text = proseBlock(section.attr, value, ctx);
  if (!text) return '';
  return `${heading(section.heading, ctx)}\n${ctx.markdown ? '\n' : ''}${text}`;
}

function sectionText(section, values, ctx) {
  switch (section.style) {
    // A stat-block heading (issue 1591): its name over what it holds, open or closed alike.
    case 'stat-heading': {
      const parts = section.sections.map((s) => sectionText(s, values, ctx)).filter(Boolean);
      return parts.length ? [heading(section.heading, ctx), ...parts].join(ctx.markdown ? '\n\n' : '\n') : '';
    }
    case 'prose': return proseSectionText(section, values, ctx);
    case 'composed-line': return composedSectionText(section, values, ctx);
    case 'abbrev-list': return abbrevSectionText(section, values, ctx);
    case 'link-list': return linkListText(section, values, ctx);
    case 'badge-links': return badgeLinksText(section, values, ctx);
    case 'table': return tableSectionText(section, values, ctx);
    case 'signed-list': {
      const text = signedListText(section, values);
      return text ? labeled(section.heading, text, ctx) : '';
    }
    case 'stat-grid': return statGridText(section, values, ctx);
    case 'inline-expand': return inlineExpandText(section, values, ctx);
    // Navigation, not entry content: a bucket grid is links to filtered lists.
    case 'bucket-by-link-data': return '';
    default: return rowsSectionText(section, values, ctx);
  }
}

// ---------------------------------------------------------------------------
// The whole entry
// ---------------------------------------------------------------------------

/**
 * @param {object} family  a derived registry entry (`getFamily`/`deriveFamily`)
 * @param {object} entry   the detail payload as fetched
 * @param {object} opts    { markdown, origin, system } — origin/system feed the
 *                         Markdown links; text mode ignores them
 */
export function formatEntry(family, entry, opts = {}) {
  const ctx = {
    markdown: opts.markdown === true,
    origin: opts.origin || '',
    system: opts.system || '',
  };
  const blocks = [];

  const name = entry[family.nameAttr.key] || entry.name || '';
  blocks.push(ctx.markdown ? `# ${name}` : String(name));

  const subtitleSection = family.detail.sections.find((s) => s.placement === 'subtitle');
  const subtitle = subtitleSection
    ? composeLine((subtitleSection.config || {}).pattern, entry) : '';
  if (subtitle) blocks.push(ctx.markdown ? `*${subtitle}*` : subtitle);

  // Header badges (CR, feat categories…) — minus anything the subtitle already
  // spelled. A badge whose config carries a `label_prefix` is self-describing
  // ("CR 1/2"); one without gets its attribute label.
  const subtitleKeys = new Set((subtitleSection?.members || []).map((a) => a.key));
  for (const attr of family.detail.headerBadges) {
    if (subtitleKeys.has(attr.key)) continue;
    const items = badgeItems(attr, entry[attr.key]);
    if (!items.length) continue;
    const text = items.map((item) => item.text).join(', ');
    blocks.push((attr.renderConfig || {}).label_prefix
      ? (ctx.markdown ? `**${text}**` : text)
      : labeled(attr.label, text, ctx));
  }

  for (const section of family.detail.sections) {
    if (section.placement === 'subtitle') continue;
    const text = sectionText(section, entry, ctx);
    if (text) blocks.push(text);
  }

  if (hasValue(entry.rulebook_name)) {
    blocks.push(ctx.markdown ? `*Source: ${entry.rulebook_name}*` : `Source: ${entry.rulebook_name}`);
  }

  return blocks.join('\n\n');
}

export function formatEntryText(family, entry, opts = {}) {
  return formatEntry(family, entry, { ...opts, markdown: false });
}

export function formatEntryMarkdown(family, entry, opts = {}) {
  return formatEntry(family, entry, { ...opts, markdown: true });
}
